import config from '../../config';
import { getAuthHeaders, getAuthHeaderOnly, handleNoContentResponse, handleResponse } from './common';

export const clientChartImportQueryKeys = {
  all: ['client-chart-imports'],
  referenceOnlyObstetricReminder: (clientReferenceId) => [
    ...clientChartImportQueryKeys.all,
    'reference-only-obstetric-reminder',
    clientReferenceId,
  ],
};

export async function invalidateReferenceOnlyObstetricReminder(queryClient, clientReferenceId) {
  if (!clientReferenceId) return;
  await queryClient.invalidateQueries({
    queryKey: clientChartImportQueryKeys.referenceOnlyObstetricReminder(clientReferenceId),
  });
}

/** Cap parallel acknowledge calls to reduce load on the API/database. */
export const ACKNOWLEDGE_REFERENCE_REVIEW_CONCURRENCY = 3;

export async function settleAllWithConcurrency(items, concurrency, handler) {
  if (!items.length) return [];

  const limit = Math.min(Math.max(1, concurrency), items.length);
  let nextIndex = 0;
  const results = new Array(items.length);

  await Promise.all(Array.from({ length: limit }, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex++;
      try {
        results[index] = { status: 'fulfilled', value: await handler(items[index], index) };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  }));

  return results;
}

const unwrapArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.$values)) return value.$values;
  return [];
};

const pick = (dto, ...keys) => {
  for (const key of keys) {
    if (dto && dto[key] !== undefined) return dto[key];
  }
  return undefined;
};

const normalizeField = (dto) => {
  if (!dto) return dto;
  return {
    referenceId: pick(dto, 'referenceId', 'ReferenceId'),
    fieldPath: pick(dto, 'fieldPath', 'FieldPath') ?? null,
    displayLabel: pick(dto, 'displayLabel', 'DisplayLabel') ?? pick(dto, 'fieldPath', 'FieldPath') ?? 'Field',
    normalizedValue: pick(dto, 'normalizedValue', 'NormalizedValue') ?? null,
    existingValueJson: pick(dto, 'existingValueJson', 'ExistingValueJson') ?? null,
    reviewerEditedValue: pick(dto, 'reviewerEditedValue', 'ReviewerEditedValue') ?? null,
    sourcePageNumber: pick(dto, 'sourcePageNumber', 'SourcePageNumber') ?? null,
    reviewStatus: pick(dto, 'reviewStatus', 'ReviewStatus') ?? null,
    requiresExplicitOverwriteApproval: pick(dto, 'requiresExplicitOverwriteApproval', 'RequiresExplicitOverwriteApproval') ?? false,
    explicitOverwriteApproved: pick(dto, 'explicitOverwriteApproved', 'ExplicitOverwriteApproved') ?? false,
  };
};

const normalizeCandidate = (dto) => {
  if (!dto) return dto;
  return {
    referenceId: pick(dto, 'referenceId', 'ReferenceId'),
    candidateType: pick(dto, 'candidateType', 'CandidateType') ?? null,
    isCommittable: pick(dto, 'isCommittable', 'IsCommittable') ?? null,
    displayLabel: pick(dto, 'displayLabel', 'DisplayLabel') ?? 'Candidate',
    displayCategory: pick(dto, 'displayCategory', 'DisplayCategory') ?? null,
    proposedAction: pick(dto, 'proposedAction', 'ProposedAction') ?? null,
    reviewStatus: pick(dto, 'reviewStatus', 'ReviewStatus') ?? null,
    duplicateStatus: pick(dto, 'duplicateStatus', 'DuplicateStatus') ?? null,
    normalizedJson: pick(dto, 'normalizedJson', 'NormalizedJson') ?? null,
    existingValueJson: pick(dto, 'existingValueJson', 'ExistingValueJson') ?? null,
    reviewerEditedJson: pick(dto, 'reviewerEditedJson', 'ReviewerEditedJson') ?? null,
    requiresExplicitOverwriteApproval: pick(dto, 'requiresExplicitOverwriteApproval', 'RequiresExplicitOverwriteApproval') ?? false,
    explicitOverwriteApproved: pick(dto, 'explicitOverwriteApproved', 'ExplicitOverwriteApproved') ?? false,
    isLowConfidence: pick(dto, 'isLowConfidence', 'IsLowConfidence') ?? false,
    confidenceReason: pick(dto, 'confidenceReason', 'ConfidenceReason') ?? null,
    sourcePageNumber: pick(dto, 'sourcePageNumber', 'SourcePageNumber') ?? null,
    sourceTextSnippet: pick(dto, 'sourceTextSnippet', 'SourceTextSnippet') ?? null,
    fields: unwrapArray(pick(dto, 'fields', 'Fields')).map(normalizeField),
  };
};

const normalizeJob = (dto) => {
  if (!dto) return dto;
  return {
    referenceId: pick(dto, 'referenceId', 'ReferenceId'),
    status: pick(dto, 'status', 'Status') ?? null,
    sourceSystem: pick(dto, 'sourceSystem', 'SourceSystem') ?? null,
    sourceDocumentReferenceId: pick(dto, 'sourceDocumentReferenceId', 'SourceDocumentReferenceId') ?? null,
    sourceFileName: pick(dto, 'sourceFileName', 'SourceFileName') ?? null,
    targetClientReferenceId: pick(dto, 'targetClientReferenceId', 'TargetClientReferenceId') ?? null,
    targetPregnancyReferenceId: pick(dto, 'targetPregnancyReferenceId', 'TargetPregnancyReferenceId') ?? null,
    email: pick(dto, 'email', 'Email', 'seedClientEmail', 'SeedClientEmail') ?? null,
    firstName: pick(dto, 'firstName', 'FirstName', 'seedClientFirstName', 'SeedClientFirstName') ?? null,
    lastName: pick(dto, 'lastName', 'LastName', 'seedClientLastName', 'SeedClientLastName') ?? null,
    assignedMidwifeReferenceId: pick(dto, 'assignedMidwifeReferenceId', 'AssignedMidwifeReferenceId') ?? null,
    seedClientEmail: pick(dto, 'seedClientEmail', 'SeedClientEmail') ?? null,
    seedClientFirstName: pick(dto, 'seedClientFirstName', 'SeedClientFirstName') ?? null,
    seedClientLastName: pick(dto, 'seedClientLastName', 'SeedClientLastName') ?? null,
    pageCount: pick(dto, 'pageCount', 'PageCount', 'totalPages', 'TotalPages') ?? 0,
    candidateCount: pick(dto, 'candidateCount', 'CandidateCount') ?? 0,
    reviewCount: pick(dto, 'reviewCount', 'ReviewCount', 'pendingCandidateCount', 'PendingCandidateCount') ?? 0,
    conflictCount: pick(dto, 'conflictCount', 'ConflictCount', 'conflictCandidateCount', 'ConflictCandidateCount') ?? 0,
    lowConfidenceCount: pick(dto, 'lowConfidenceCount', 'LowConfidenceCount', 'lowConfidenceCandidateCount', 'LowConfidenceCandidateCount') ?? 0,
    approvedCount: pick(dto, 'approvedCount', 'ApprovedCount', 'approvedCandidateCount', 'ApprovedCandidateCount') ?? 0,
    editedCount: pick(dto, 'editedCount', 'EditedCount', 'editedCandidateCount', 'EditedCandidateCount') ?? 0,
    rejectedCount: pick(dto, 'rejectedCount', 'RejectedCount', 'rejectedCandidateCount', 'RejectedCandidateCount') ?? 0,
    skippedCount: pick(dto, 'skippedCount', 'SkippedCount', 'skippedCandidateCount', 'SkippedCandidateCount') ?? 0,
    committedCount: pick(dto, 'committedCount', 'CommittedCount') ?? 0,
    failureReason: pick(dto, 'failureReason', 'FailureReason') ?? null,
    createdAt: pick(dto, 'createdAt', 'CreatedAt') ?? null,
    extractedAt: pick(dto, 'extractedAt', 'ExtractedAt') ?? null,
    parsedAt: pick(dto, 'parsedAt', 'ParsedAt') ?? null,
    reviewedAt: pick(dto, 'reviewedAt', 'ReviewedAt') ?? null,
    committedAt: pick(dto, 'committedAt', 'CommittedAt') ?? null,
  };
};

const normalizeSummary = (dto) => {
  if (!dto) return dto;
  const countsByType = pick(dto, 'countsByCandidateType', 'CountsByCandidateType') ?? {};
  const countsByStatus = pick(dto, 'countsByReviewStatus', 'CountsByReviewStatus') ?? {};
  const statusCount = (status) => countsByStatus[status] ?? countsByStatus[status.toLowerCase()] ?? 0;
  return {
    ...dto,
    importJobReferenceId: pick(dto, 'importJobReferenceId', 'ImportJobReferenceId') ?? pick(dto, 'referenceId', 'ReferenceId'),
    status: pick(dto, 'status', 'Status') ?? null,
    candidateCount: pick(dto, 'candidateCount', 'CandidateCount') ?? Object.values(countsByType).reduce((total, count) => total + count, 0),
    approvedCount: pick(dto, 'approvedCount', 'ApprovedCount') ?? statusCount('Approved'),
    editedCount: pick(dto, 'editedCount', 'EditedCount') ?? statusCount('Edited'),
    rejectedCount: pick(dto, 'rejectedCount', 'RejectedCount') ?? statusCount('Rejected'),
    skippedCount: pick(dto, 'skippedCount', 'SkippedCount') ?? statusCount('Skipped'),
  };
};

const normalizeReview = (dto) => {
  if (!dto) return dto;
  return {
    importJobReferenceId: pick(dto, 'importJobReferenceId', 'ImportJobReferenceId') ?? null,
    status: pick(dto, 'status', 'Status') ?? null,
    demographics: normalizeCandidate(pick(dto, 'demographics', 'Demographics', 'demographicsCandidate', 'DemographicsCandidate')),
    prenatalVisits: unwrapArray(pick(dto, 'prenatalVisits', 'PrenatalVisits')).map(normalizeCandidate),
    additionalCandidates: unwrapArray(pick(dto, 'additionalCandidates', 'AdditionalCandidates')).map(normalizeCandidate),
    issues: unwrapArray(pick(dto, 'issues', 'Issues')),
  };
};

const normalizeCommitResult = (dto) => {
  if (!dto) return dto;
  return {
    ...dto,
    importJobReferenceId: pick(dto, 'importJobReferenceId', 'ImportJobReferenceId') ?? null,
    status: pick(dto, 'status', 'Status') ?? null,
    clientReferenceId: pick(dto, 'clientReferenceId', 'ClientReferenceId') ?? null,
    pregnancyReferenceId: pick(dto, 'pregnancyReferenceId', 'PregnancyReferenceId') ?? null,
    committedCount: pick(dto, 'committedCount', 'CommittedCount', 'committedCandidateCount', 'CommittedCandidateCount') ?? 0,
    skippedCount: pick(dto, 'skippedCount', 'SkippedCount', 'skippedCandidateCount', 'SkippedCandidateCount') ?? 0,
  };
};

const buildPatchPayload = ({ reviewStatus, reviewerEditedJson, fields, explicitOverwriteApproved }) => ({
  reviewStatus,
  reviewerEditedJson: reviewerEditedJson ?? null,
  explicitOverwriteApproved: explicitOverwriteApproved ?? false,
  fields: unwrapArray(fields).map((field) => ({
    referenceId: field.referenceId,
    reviewerEditedValue: field.reviewerEditedValue ?? null,
    reviewStatus: field.reviewStatus ?? reviewStatus,
    explicitOverwriteApproved: field.explicitOverwriteApproved ?? false,
  })),
});

const clientChartImportApi = {
  listImportJobs: async ({ take = 25, targetClientReferenceId, completedOnly = false, withReferenceOnlyObstetricData = false } = {}) => {
    const params = new URLSearchParams({ take: String(take) });
    if (targetClientReferenceId) params.set('targetClientReferenceId', targetClientReferenceId);
    if (completedOnly) params.set('completedOnly', 'true');
    if (withReferenceOnlyObstetricData) params.set('withReferenceOnlyObstetricData', 'true');
    const res = await fetch(`${config.api.url}/client-chart-imports?${params}`, {
      headers: getAuthHeaders(),
    });
    return unwrapArray(await handleResponse(res)).map(normalizeJob);
  },

  createImportJob: async ({
    file,
    email,
    firstName,
    lastName,
    targetClientReferenceId,
    targetPregnancyReferenceId,
    assignedMidwifeReferenceId,
  }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('email', email);
    formData.append('firstName', firstName);
    formData.append('lastName', lastName);
    if (targetClientReferenceId) formData.append('targetClientReferenceId', targetClientReferenceId);
    if (targetPregnancyReferenceId) formData.append('targetPregnancyReferenceId', targetPregnancyReferenceId);
    if (assignedMidwifeReferenceId) formData.append('assignedMidwifeReferenceId', assignedMidwifeReferenceId);

    const res = await fetch(`${config.api.url}/client-chart-imports`, {
      method: 'POST',
      headers: getAuthHeaderOnly(),
      body: formData,
    });
    return normalizeJob(await handleResponse(res));
  },

  importMnRoster: async ({ file, assignedMidwifeReferenceId }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (assignedMidwifeReferenceId) {
      formData.append('assignedMidwifeReferenceId', assignedMidwifeReferenceId);
    }

    const res = await fetch(`${config.api.url}/client-chart-imports/mn-roster`, {
      method: 'POST',
      headers: getAuthHeaderOnly(),
      body: formData,
    });
    return handleResponse(res);
  },

  getImportJob: async (importJobReferenceId) => {
    const res = await fetch(`${config.api.url}/client-chart-imports/${importJobReferenceId}`, {
      headers: getAuthHeaders(),
    });
    return normalizeJob(await handleResponse(res));
  },

  getSummary: async (importJobReferenceId) => {
    const res = await fetch(`${config.api.url}/client-chart-imports/${importJobReferenceId}/summary`, {
      headers: getAuthHeaders(),
    });
    return normalizeSummary(await handleResponse(res));
  },

  getReview: async (importJobReferenceId) => {
    const res = await fetch(`${config.api.url}/client-chart-imports/${importJobReferenceId}/review`, {
      headers: getAuthHeaders(),
    });
    return normalizeReview(await handleResponse(res));
  },

  getSourceUrl: async (importJobReferenceId) => {
    const res = await fetch(`${config.api.url}/client-chart-imports/${encodeURIComponent(importJobReferenceId)}/source-url`, {
      headers: getAuthHeaders(),
      cache: 'no-store',
    });
    return handleResponse(res);
  },

  updateCandidate: async (importJobReferenceId, candidateReferenceId, patch) => {
    const res = await fetch(`${config.api.url}/client-chart-imports/${importJobReferenceId}/candidates/${candidateReferenceId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(buildPatchPayload(patch)),
    });
    return normalizeCandidate(await handleResponse(res));
  },

  bulkReview: async (importJobReferenceId, { approveSafeNoConflictOnly = true, candidateReferenceIds = [] } = {}) => {
    const res = await fetch(`${config.api.url}/client-chart-imports/${importJobReferenceId}/bulk-review`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ approveSafeNoConflictOnly, candidateReferenceIds }),
    });
    return handleResponse(res);
  },

  commit: async (importJobReferenceId) => {
    const res = await fetch(`${config.api.url}/client-chart-imports/${importJobReferenceId}/commit`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return normalizeCommitResult(await handleResponse(res));
  },

  cancel: async (importJobReferenceId) => {
    const res = await fetch(`${config.api.url}/client-chart-imports/${importJobReferenceId}/cancel`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return normalizeJob(await handleResponse(res));
  },

  acknowledgeReferenceReview: async (importJobReferenceId) => {
    const res = await fetch(
      `${config.api.url}/client-chart-imports/${encodeURIComponent(importJobReferenceId)}/acknowledge-reference-review`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      },
    );
    return handleNoContentResponse(res);
  },
};

export default clientChartImportApi;
